import React, { useState, useEffect } from 'react';

const FINAL_ROUND_NUMBER = 30;

// Staggered fade-in animation styles
const fadeInStyle = (visible, delay = 0) => ({
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(8px)',
  transition: `opacity 350ms ease ${delay}ms, transform 350ms ease ${delay}ms`
});

export function ResultOverlay({
  summary,
  roundNumber,
  overlayFade,
  onNewGame,
  onContinueCampaign,
  onAcknowledge
}) {
  const resultSummary = summary || { winner: 'Draw', reason: 'Game over' };
  const isLoss = !!resultSummary.playerLost;
  const isCampaignWin = !!resultSummary.campaignComplete;
  const defaultCampaignReason = `You cleared all ${FINAL_ROUND_NUMBER} rounds!`;
  
  // Animation state for staggered reveal
  const [animateIn, setAnimateIn] = useState(false);
  
  useEffect(() => {
    if (overlayFade) {
      // Small delay to ensure CSS transitions trigger properly
      const timer = setTimeout(() => setAnimateIn(true), 50);
      return () => clearTimeout(timer);
    } else {
      setAnimateIn(false);
    }
  }, [overlayFade]);

  // Determine display values for current round and best round
  const displayRound = resultSummary.currentRound || roundNumber || 1;
  let bestFromStorage = 0;
  try {
    bestFromStorage = parseInt((window.localStorage.getItem('gc_best_round') || '0'), 10) || 0;
  } catch (_) {
    bestFromStorage = 0;
  }
  const displayBest = resultSummary.bestRound != null ? resultSummary.bestRound : bestFromStorage;
  const titleText = resultSummary.winner === 'Draw'
    ? 'Draw'
    : (isLoss ? 'You lost' : (isCampaignWin ? 'You win!' : `${resultSummary.winner} wins`));
  const reasonText = isCampaignWin ? (resultSummary.reason || defaultCampaignReason) : (resultSummary.reason || 'Game over');
  const showEconomySummary = !isLoss && !isCampaignWin && resultSummary.totalGain != null;
  const showProgress = isLoss || isCampaignWin;
  const progressLabel = isCampaignWin
    ? `Round ${displayRound} cleared - Best ${displayBest}`
    : `Round ${displayRound} - Best ${displayBest}`;
  const restartBtnStyle = { padding: '12px 24px', fontSize: 18, cursor: 'pointer' };

  // Calculate stagger delays
  const baseDelay = 100;
  const staggerIncrement = 200;
  let currentDelay = baseDelay;
  
  const getNextDelay = () => {
    const delay = currentDelay;
    currentDelay += staggerIncrement;
    return delay;
  };

  if (isCampaignWin) {
    // Reset delay counter for campaign win screen
    currentDelay = baseDelay;
    
    return (
      <div style={{ position: 'absolute', top: 0, left: 0, width: 560, height: 560, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', opacity: overlayFade ? 1 : 0, transition: 'opacity 250ms ease' }} />
        <div style={{ background: '#fff', color: '#111', padding: 16, borderRadius: 8, minWidth: 320, textAlign: 'center', position: 'relative' }}>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 10, ...fadeInStyle(animateIn, getNextDelay()) }}>You win!</div>
          <div style={{ fontSize: 15, marginBottom: 18, ...fadeInStyle(animateIn, getNextDelay()) }}>{`You cleared ${displayRound} rounds.`}</div>
          <div style={{ marginBottom: 16, ...fadeInStyle(animateIn, getNextDelay()) }}>
            <button onClick={onNewGame} style={{ padding: '12px 24px', fontSize: 18, cursor: 'pointer' }}>New Game</button>
          </div>
          <div style={{ fontSize: 14, opacity: animateIn ? 0.8 : 0, marginBottom: 12, transform: animateIn ? 'translateY(0)' : 'translateY(8px)', transition: `opacity 350ms ease ${getNextDelay()}ms, transform 350ms ease ${currentDelay - staggerIncrement}ms` }}>{`Best run: ${displayBest} rounds.`}</div>
          <div style={{ ...fadeInStyle(animateIn, getNextDelay()) }}>
            <button onClick={onContinueCampaign} style={{ padding: '8px 14px', fontSize: 14, cursor: 'pointer' }}>
              Continue Playing...
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Reset delay counter for normal result screen
  currentDelay = baseDelay;
  
  // Pre-calculate delays for the main result overlay
  const titleDelay = getNextDelay();
  const reasonDelay = getNextDelay();
  const economyDelay = showEconomySummary ? getNextDelay() : 0;
  const progressDelay = showProgress ? getNextDelay() : 0;
  const buttonDelay = getNextDelay();

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: 560, height: 560, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', opacity: overlayFade ? 1 : 0, transition: 'opacity 250ms ease' }} />
      <div style={{ background: '#fff', color: '#111', padding: 16, borderRadius: 8, minWidth: 320, textAlign: 'center', position: 'relative' }}>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 6, ...fadeInStyle(animateIn, titleDelay) }}>{titleText}</div>
        <div style={{ fontSize: 14, marginBottom: 12, ...fadeInStyle(animateIn, reasonDelay), opacity: animateIn ? 0.8 : 0 }}>{reasonText}</div>
        {showEconomySummary ? (
          <div style={{ fontSize: 14, marginBottom: 10, ...fadeInStyle(animateIn, economyDelay) }}>
            <div style={{ fontSize: 16, fontWeight: 800, textAlign: 'center' }}>{`You gain $${resultSummary.totalGain}`}</div>
            <div style={{ maxWidth: 160, margin: '6px auto 0', textAlign: 'left', borderTop: '1px solid #ddd', paddingTop: 6, color: '#555' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>{`Income for ${resultSummary.resultType === 'win' ? 'win' : 'draw'}`}</div>
                <div>{`$${resultSummary.baseGain || 0}`}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>Interest</div>
                <div>{`$${resultSummary.incomeGain || 0}`}</div>
              </div>
              {(() => {
                const aug = resultSummary.rewardDelta || 0;
                if (!aug) return null;
                return (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>Augments</div>
                    <div>{`$${aug}`}</div>
                  </div>
                );
              })()}
            </div>
          </div>
        ) : null}
        {showProgress ? (
          <div style={{ fontSize: 14, marginBottom: 12, ...fadeInStyle(animateIn, progressDelay), opacity: animateIn ? 0.8 : 0 }}>{progressLabel}</div>
        ) : null}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', ...fadeInStyle(animateIn, buttonDelay) }}>
          {showProgress ? (
            <button onClick={onNewGame} style={restartBtnStyle}>New Game</button>
          ) : (
            <button onClick={onAcknowledge} style={{ padding: '10px 18px', fontSize: 17, cursor: 'pointer' }}>
              {resultSummary.winner === 'White' ? 'Next Round' : 'Continue'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
